import React, { FunctionComponent, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { Box, Text, Accordion, accordionItemBuilder } from '@wix/design-system';
import { Category } from '@wix/answers-api';
import { DATA_HOOKS } from '../../dataHooks';
import { isRTL } from '../../utils/internationalization';
import { ArrowDownRightIcon } from '../../icons/ArrowDownRightIcon';
import { ArrowDownLeftIcon } from '../../icons/ArrowDownLeftIcon';
import css from './index.module.scss';

type CategoryTreeViewerProps = {
  categories: Category[];
  currentCategoryId?: string;
  currentSubCategoryId?: string;
  onSubCategoryChange: (subCategory: Category, order: string) => void;
  onParentCategoryChange?: (category: Category, order: string) => void;
};

export const CategoryTreeViewer: FunctionComponent<CategoryTreeViewerProps> = ({
  categories,
  currentCategoryId,
  currentSubCategoryId,
  onSubCategoryChange,
  onParentCategoryChange,
}) => {
  const { locale } = useRouter();
  const [currentOpenCategoryId, setCurrentOpenCategoryId] = useState<
    string | undefined
  >(currentCategoryId);
  const isRTLLanguage = isRTL(locale);

  const isHasChildren = (children?: Category[]) =>
    children && children.length > 0;

  return (
    <div className={css.categoryTree}>
      <Accordion
        dataHook={DATA_HOOKS.CATEGORIES_LIST_ACCORDION}
        items={categories.map((category, parentIndex) =>
          accordionItemBuilder({
            title: category.name,
            onToggle: () =>
              !isHasChildren(category.children) && onParentCategoryChange
                ? onParentCategoryChange(category, `${parentIndex + 1}`)
                : setCurrentOpenCategoryId(
                    currentOpenCategoryId === category.id
                      ? undefined
                      : category.id
                  ),
            className: css.categoryItem,
            open: category.id === currentOpenCategoryId,
            buttonType: 'node',
            showLabel: 'always',
            expandLabel: isHasChildren(category.children) ? (
              <Image
                src={`https://static.parastorage.com/services/kb-client-nextjs/dist/public/images/icons/chevron-down.svg`}
                height={18}
                width={18}
                alt=""
              />
            ) : null,
            collapseLabel: (
              <Image
                src={`https://static.parastorage.com/services/kb-client-nextjs/dist/public/images/icons/minus.svg`}
                width="18"
                height="18"
                alt=""
              />
            ),
            children: (
              <>
                {category.children?.map((child, index) => (
                  <Box
                    className={css.categoryChildren}
                    key={child.id}
                    gap="6px"
                  >
                    {isRTLLanguage ? (
                      <ArrowDownLeftIcon />
                    ) : (
                      <ArrowDownRightIcon />
                    )}
                    <Text
                      onClick={() => onSubCategoryChange(child, `${index + 1}`)}
                      className={`${css.categoryTreeViewerItem} ${
                        child.id === currentSubCategoryId ? css.focus : ''
                      }`}
                      size="medium"
                      maxLines={1}
                      ellipsis
                      disabled
                      dataHook={DATA_HOOKS.CATEGORY_TREE_VIEWER_ITEM}
                    >
                      {child.name}
                    </Text>
                  </Box>
                ))}
              </>
            ),
          })
        )}
        horizontalPadding="none"
        size="medium"
      />
    </div>
  );
};
